import {
    MinMaxGradeRange,
    MinMaxGradingScale,
    NearestAdaptableRange,
    PercentageGradeRange,
    PercentageGradingScale,
} from "p6m-p6u";
import { Scoring } from "../constants/ScoringConstants";
import { SettingsDefault } from "../constants/SettingsConstants";
import { EditScoreDirection } from "../enums/directions";
import { GradeRangeLimitType, GradingScaleFormat } from "../enums/settings";

// for debugging the grading scale only
// export const logGradingScale = (acc?: MinMaxGradingScale) => {
//     if (!acc) {
//         console.log(" - - no grading scale - -");
//         return;
//     }

//     let line1 = "";
//     let line2 = "";

//     acc.forEach((element) => {
//         line1 = line1 + `|     ${element.grade}     `;
//     });
//     acc.forEach((element) => {
//         line2 = line2 + `| ${element.maxScore.toFixed(1)} - ${element.minScore.toFixed(1)} `;
//     });

//     console.log(line1);
//     console.log(line2);
// };

type EditGradeRangeParams = {
    gradingScale: MinMaxGradingScale;
    shiftingTarget: number;
    index: number;
    minOrMax: GradeRangeLimitType;
    stepSize: number;
};

type UpdateGradeRangeParams = {
    gradingScale: MinMaxGradingScale;
    nearestAdaptableRanges: NearestAdaptableRange[];
    index: number;
    minOrMax: GradeRangeLimitType;
    direction: EditScoreDirection;
    format: GradingScaleFormat;
};

type RedistributionParams = {
    gradingScale: MinMaxGradingScale;
    startIndex: number;
    shiftingTarget: number;
    stepSize: number;
};

export const createPercentageFromMinMax = (gradingScale: MinMaxGradingScale) => {
    const overallScore = gradingScale[0].maxScore;
    const percentageGradingScale = gradingScale.reduce(
        (percentageScoreRanges: PercentageGradeRange[], scaleElement: MinMaxGradeRange) => {
            const minScorePercentage = (scaleElement.minScore / overallScore) * 100;

            percentageScoreRanges.push({
                grade: scaleElement.grade,
                minScorePercentage,
            });

            return percentageScoreRanges;
        },
        []
    );

    return percentageGradingScale;
};

const _createMinMaxPercentageToScore = (overallScore: number, gradingScale: PercentageGradingScale) => {
    const doubleOverallScore = (overallScore ?? 100) * 2; // to be able to round to .5

    const minMaxGradingScale = gradingScale.reduce(
        (minMaxScores: MinMaxGradeRange[], scaleElement: PercentageGradeRange, index: number) => {
            const minPercentage = scaleElement.minScorePercentage / 100;

            const minScore = Math.round(minPercentage * doubleOverallScore) / 2; // "rounding" to .5

            const maxScore =
                index === 0
                    ? overallScore // first element's max is overall score of test
                    : Math.max(minMaxScores[index - 1].minScore - Scoring.POINTS_STEP, 0); // max is previous min - 0.5

            minMaxScores.push({
                grade: scaleElement.grade,
                minScore,
                maxScore,
            });

            return minMaxScores;
        },
        []
    );

    return minMaxGradingScale;
};

const _createMinMaxInPercentage = (gradingScale: PercentageGradingScale) => {
    const minMaxGradingScale = gradingScale.reduce(
        (minMaxScores: MinMaxGradeRange[], scaleElement: PercentageGradeRange, index: number) => {
            // rounding all because saved percentages might have lots of decimal places
            const minScore = Math.round(scaleElement.minScorePercentage);
            const maxScore =
                index === 0
                    ? 100 // first element's max is 100%
                    : Math.max(Math.round(minMaxScores[index - 1].minScore) - 1, 0); // max is previous min - 1

            minMaxScores.push({
                grade: scaleElement.grade,
                minScore,
                maxScore,
            });

            return minMaxScores;
        },
        []
    );

    return minMaxGradingScale;
};

const _increaseGradeRangeValue = (editGradeRangeParams: EditGradeRangeParams) => {
    const { gradingScale, shiftingTarget, index, minOrMax, stepSize } = editGradeRangeParams;

    if (!gradingScale || gradingScale.length === 0) return gradingScale;

    const gradeRange = gradingScale[index];
    const isEditMin = minOrMax === GradeRangeLimitType.MIN;
    const isRangeReduction = isEditMin && gradeRange.minScore < gradeRange.maxScore;

    let newScale = [...gradingScale];
    if (isRangeReduction) {
        // update current range
        newScale[index].minScore += stepSize;

        if (index + 1 < gradingScale.length) {
            // follow with next grade range
            newScale[index + 1].maxScore += stepSize;
        }
    } else if (shiftingTarget >= 0 && shiftingTarget <= index) {
        // update current range
        if (isEditMin) newScale[index].minScore += stepSize;
        newScale[index].maxScore += stepSize;

        // shifting necessary
        newScale = _redistributeScoresLeft({ gradingScale, startIndex: index - 1, shiftingTarget, stepSize });
    }
    return newScale;
};

const _decreaseGradeRangeValue = (editGradeRangeParams: EditGradeRangeParams) => {
    const { gradingScale, shiftingTarget, index, minOrMax, stepSize } = editGradeRangeParams;

    if (!gradingScale || gradingScale.length === 0) return gradingScale;

    const gradeRange = gradingScale[index];
    const isEditMax = minOrMax === GradeRangeLimitType.MAX;
    const isRangeReduction = isEditMax && gradeRange.minScore < gradeRange.maxScore;

    let newScale = [...gradingScale];
    if (isRangeReduction) {
        // update current range
        newScale[index].maxScore -= stepSize;

        if (index - 1 >= 0) {
            // follow with previous range
            newScale[index - 1].minScore -= stepSize;
        }
    } else if (shiftingTarget >= 0 && shiftingTarget >= index) {
        //update current range
        if (isEditMax) newScale[index].maxScore -= stepSize;
        newScale[index].minScore = newScale[index].minScore - stepSize;

        //shifting necessary
        newScale = _redistributeScoresRight({
            gradingScale,
            startIndex: index + 1,
            shiftingTarget,
            stepSize,
        });
    }
    return newScale;
};

export const updateGradeRange = (updateGradeRangeParams: UpdateGradeRangeParams) => {
    const { gradingScale, index, minOrMax, direction, nearestAdaptableRanges, format } = updateGradeRangeParams;

    const stepSize = format === GradingScaleFormat.PERCENTAGE ? 1 : Scoring.POINTS_STEP;
    let newScale: MinMaxGradingScale = [...gradingScale];

    if (direction === EditScoreDirection.INCREMENT) {
        newScale = _increaseGradeRangeValue({
            gradingScale,
            shiftingTarget: nearestAdaptableRanges[index].left,
            index,
            minOrMax,
            stepSize,
        });
    } else {
        newScale = _decreaseGradeRangeValue({
            gradingScale,
            shiftingTarget: nearestAdaptableRanges[index].right,
            index,
            minOrMax,
            stepSize,
        });
    }

    return newScale;
};

export const findAllAdaptableRangeTargets = (gradingScale: MinMaxGradingScale): NearestAdaptableRange[] => {
    let adaptableRanges = gradingScale.reduce((acc: NearestAdaptableRange[], _gradeRange: MinMaxGradeRange) => {
        acc.push({ left: -1, right: -1 });
        return acc;
    }, []);

    gradingScale.forEach((gradeRange: MinMaxGradeRange, index: number) => {
        const hasAdjustableRange = gradeRange.maxScore > gradeRange.minScore;
        if (hasAdjustableRange) {
            // add to all on left
            if (index - 1 >= 0) {
                for (let i = index - 1; i >= 0; i--) {
                    if (adaptableRanges[i].right === -1) adaptableRanges[i].right = index; // always write, because this is the closest
                }
            }

            // add to all on right
            if (index + 1 < gradingScale.length) {
                for (let j = index + 1; j < gradingScale.length; j++) {
                    adaptableRanges[j].left = index; // only set if not yet, because older is closer
                }
            }
        }
    });

    return adaptableRanges;
};

const _findAdaptableRangeTarget = (gradingScale: MinMaxGradingScale, i: number, direction: "RIGHT" | "LEFT") => {
    const startingPointRight = i + 1 < gradingScale.length ? i + 1 : i;
    const startingPointLeft = i - 1 >= 0 ? i - 1 : i;

    const startingPoint = direction === "RIGHT" ? startingPointRight : startingPointLeft;

    let index = -1;
    for (
        let j = startingPoint;
        direction === "RIGHT" ? j <= gradingScale.length - 1 : j >= 0;
        direction === "RIGHT" ? j++ : j--
    ) {
        const gradeRange = gradingScale[j];

        const hasAdjustableRange = gradeRange.maxScore > gradeRange.minScore;
        if (hasAdjustableRange) {
            index = j;
            break;
        }
    }

    return index;
};

const _redistributeScoresRight = (redistributionParams: RedistributionParams) => {
    const { gradingScale, startIndex, shiftingTarget, stepSize } = redistributionParams;

    // move current grade and all grades to the right down by stepSize (0.5 for scores, 1 for percent)
    // and make target take up less space by not moving its min
    const cleanedUpGradingScale = [...gradingScale];
    for (let j = startIndex; j <= shiftingTarget; j++) {
        cleanedUpGradingScale[j].maxScore -= stepSize; // go down

        if (j !== shiftingTarget) {
            // follow with min unless it's the target
            cleanedUpGradingScale[j].minScore -= stepSize;
        }
    }
    return cleanedUpGradingScale;
};

const _redistributeScoresLeft = (redistributionParams: RedistributionParams) => {
    const { gradingScale, startIndex, shiftingTarget, stepSize } = redistributionParams;

    // move all grades left to us up by stepSize (0.5 for scores, 1 for percent)
    // and make target take up less space by not moving its max
    const cleanedUpGradingScale = [...gradingScale];
    for (let j = startIndex; j >= shiftingTarget; j--) {
        cleanedUpGradingScale[j].minScore += stepSize; // go up

        if (j > shiftingTarget) {
            // follow with max unless it's the target
            cleanedUpGradingScale[j].maxScore += stepSize;
        }
    }

    return cleanedUpGradingScale;
};

/**
 * Cleans up the grading scale to avoid duplicate or overlapping score ranges:
 * - The last element's minScore is always 0
 * - No other minScore is 0
 * - Ensures minScore <= maxScore for all elements
 * - Fixes overlapping ranges by redistributing scores accordingly
 *
 * A visual representation of the redistribution logic can be found in our how to <3
 * https://horizon-alpha.atlassian.net/wiki/spaces/LM/pages/2969993238/AEA+Lehrkr+ftetool#_cleanUpGradingScale
 */
const _cleanUpGradingScale = (gradingScale: MinMaxGradingScale, stepSize: number) => {
    let cleanedUpGradingScale = [...gradingScale];

    //____________BASE CLEAN UP ___________________________________________
    // - last element's min should always be 0
    if (cleanedUpGradingScale[cleanedUpGradingScale.length - 1].minScore !== 0) {
        cleanedUpGradingScale[cleanedUpGradingScale.length - 1].minScore = 0;
    }

    //____________FIRST CLEAN UP CYCLE ____________________________________
    cleanedUpGradingScale.forEach((gradeRange, i) => {
        if (gradeRange.minScore === 0 && i !== cleanedUpGradingScale.length - 1) {
            // - no other minScore should be 0 than the last
            gradeRange.minScore = stepSize;
        }

        if (gradeRange.minScore > gradeRange.maxScore) {
            // - minScore <= maxScore for all elemenets
            gradeRange.maxScore = gradeRange.minScore;
        }
    });

    //____________SECOND CLEAN UP CYCLE ____________________________________
    // - Redistributes scores if necessary to avoid overlaps and duplicates
    const rangesToCleanUp = cleanedUpGradingScale.some((gradeRange: MinMaxGradeRange, i: number) => {
        return i > 0 && cleanedUpGradingScale[i - 1].minScore <= gradeRange.maxScore; // current max score overlaps with previous min score
    });

    if (!rangesToCleanUp) return cleanedUpGradingScale;

    // fix ranges from back to front
    for (let i = cleanedUpGradingScale.length - 1; i >= 0; i--) {
        let gradeRange = cleanedUpGradingScale[i];

        if (i === 0 || cleanedUpGradingScale[i - 1].minScore > gradeRange.maxScore) {
            continue; // first element or okay
        }

        // fix overlapping range ___________________________________________
        // current max score overlaps with previous min score

        // attempt an easy fix: can we move max closer to min?
        if (gradeRange.maxScore - stepSize >= gradeRange.minScore) {
            gradeRange.maxScore = gradeRange.maxScore - stepSize;
            continue;
        }

        // elements to the right have already been cleaned up
        // --> find an element to the right that can make space for us
        const targetElementIndexRight = _findAdaptableRangeTarget(cleanedUpGradingScale, i, "RIGHT");

        if (targetElementIndexRight >= 0) {
            // there is such an element
            cleanedUpGradingScale = _redistributeScoresRight({
                gradingScale: cleanedUpGradingScale,
                startIndex: i,
                shiftingTarget: targetElementIndexRight,
                stepSize,
            });
        } else {
            // there is no such element
            // -> find an element to the left that can make space for us
            const targetElementIndexLeft = _findAdaptableRangeTarget(cleanedUpGradingScale, i, "LEFT");

            if (targetElementIndexLeft >= 0) {
                // there is such an element
                // (if there is none, we cannot clean up this element and need to leave this up to the fallback)

                cleanedUpGradingScale = _redistributeScoresLeft({
                    gradingScale: cleanedUpGradingScale,
                    startIndex: i - 1,
                    shiftingTarget: targetElementIndexLeft,
                    stepSize,
                });
            }
        }
    }

    return cleanedUpGradingScale;
};

const _validateGradingScale = (gradingScale: MinMaxGradingScale, stepSize: number) => {
    const isError = gradingScale.some((gradeRange: MinMaxGradeRange, index: number) => {
        if (gradeRange.minScore > gradeRange.maxScore) return true; // min max error
        if (index > 0) {
            const gap = gradingScale[index - 1].minScore - gradeRange.maxScore;
            return gap !== stepSize; // distribution error
        }
        return false; // no error found
    });

    return !isError;
};

export const convertToMinMaxGradingScale = (
    overallScore: number,
    gradingScale: PercentageGradingScale,
    format: GradingScaleFormat
) => {
    const isPercent = format === GradingScaleFormat.PERCENTAGE;
    const stepSize = isPercent ? 1 : Scoring.POINTS_STEP;

    const minMaxGradingScale = isPercent
        ? _createMinMaxInPercentage(gradingScale)
        : _createMinMaxPercentageToScore(overallScore, gradingScale);

    const cleanedUpGradingScale = _cleanUpGradingScale(minMaxGradingScale, stepSize);

    const isGradingScaleOk = _validateGradingScale(cleanedUpGradingScale, stepSize);

    if (isGradingScaleOk) {
        return cleanedUpGradingScale;
    } else {
        // fallback to settings default, in case personal default is messy
        const newMinMaxGradingScale = isPercent
            ? _createMinMaxInPercentage(SettingsDefault.gradingAndSignatures.gradingScale.scale)
            : _createMinMaxPercentageToScore(overallScore, SettingsDefault.gradingAndSignatures.gradingScale.scale);

        return _cleanUpGradingScale(newMinMaxGradingScale, stepSize);
    }
};
